import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useRef, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  getHighestItemOrderNumber,
  getOrderedItems,
} from 'components/Form/DraggableFields';
import { RelativeDatePicker } from 'components/FormFields';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';
import CategorySelect from 'components/FormFields/CategorySelect';
import SearchableSelect from 'components/FormFields/SearchableSelect';
import Toggle from 'components/FormFields/Toggle';
import {
  PageWrapper,
  Container,
  PageHeader,
  HeaderLeft,
  HeaderRight,
  BackButtonWrapper,
  Title,
  Subtitle,
  HeaderButtonWrapper,
  StepperContainer,
  StepperTrack,
  StepItem,
  StepCircle,
  StepLabel,
  StepConnector,
  StepNavigation,
  NavButtonWrapper,
  TwoColumnGrid,
  FormCard,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormRow,
  FormGroup,
  Label,
  LabelWithTooltip,
  RequiredBadge,
  Input,
  Textarea,
  Select,
  ErrorText,
  ToggleRow,
  ToggleCard,
  ToggleInfo,
  ToggleTitle,
  ToggleDescription,
  SalesWindowsContainer,
  SalesWindowCard,
  SalesWindowHeader,
  SalesWindowTitle,
  SmallIconButtonWrapper,
  AddWindowButtonWrapper,
  EmptyWindowsMessage,
  TierSection,
  TierHeader,
  TierTitle,
  AddTierButtonWrapper,
  TierRow,
  EmptyTiersMessage,
  RemoveTierButtonWrapper,
  PagesContainer,
  PagesListSection,
  PagesOrderSection,
  PageCard,
  PageCardHeader,
  PageCardTitle,
  DeletePageButtonWrapper,
  AddPageButtonWrapper,
  EmptyPagesMessage,
  OrderSectionTitle,
  DraggablePageItem,
  DragHandle,
  PageOrderName,
  WaiversContainer,
  EmptyText,
  WaiverRow,
  RemoveWaiverButtonWrapper,
  AddWaiverButtonWrapper,
  ArrowLeftIcon,
  PlusIcon,
  XIcon,
  ArrowRightIcon,
  CheckIcon,
  DragHandleIcon,
  TrashIcon,
  MinusIcon,
  TICKET_TYPES,
  VISIBILITY_OPTIONS,
  getDefaultTicketFees,
  getDefaultPosFees,
  useFormstackSearch,
} from 'components/TicketForms';
import Toaster from 'components/Toaster';
import Tooltip from 'components/Tooltip';
import { GET_WAIVERS } from 'pages/app/Events/EventEdit/WaiversRepeater/Waivers.queries';

// Query to fetch seating charts
const GET_SEATING_CHARTS = gql`
  query GetSeatingCharts {
    getSeatingCharts {
      id
      name
    }
  }
`;

// GraphQL mutation for creating ticket template
const ADD_TICKET_TEMPLATE = gql`
  mutation AddTicketTemplate($input: CreateTicketTemplateInput!) {
    addTicketTemplate(input: $input) {
      id
      name
      type
      price
      track_price
      description
      description_video
      description_image_id
      fee_dollar
      fee_percent
      pos_fee_dollar
      pos_fee_percent
      limit
      visibility
      reserved_seat
      reserved_seat_category
      reserved_seat_prefix
      reserved_seat_section
      category_id
      chart_id
      set_member_number
      prefix_text
      suffix_text
      last_member_number
      windows {
        id
        name
        ends
        time
        price
        tiers {
          id
          name
          price
          track_price
        }
      }
      tiers {
        id
        name
        price
        track_price
      }
      pages {
        id
        name
        pdf_url
        video_url
        pdf_id
        form_id
        order
        waivers {
          id
          waiver_id
        }
      }
    }
  }
`;
const AddTicketTemplateContent = () => {
  const { id: accountId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef(null);
  const theme = useTheme();
  const infoIconColor =
    theme?.colors?.textGray?.dark ||
    (theme?.name === 'dark' ? '#e2e8f0' : '#64748b');

  const [formData, setFormData] = useState({
    name: '',
    type: 'ticket',
    price: '',
    track_price: '',
    description: '',
    description_image_id: null,
    description_image_url: null, // For preview only
    description_video: '',
    fee_dollar: '',
    fee_percent: '',
    pos_fee_dollar: '',
    pos_fee_percent: '',
    visibility: 'visible',
    limit: '',
    is_featured: false,
    featured_text: '',
    category_id: '',
    // Reserved seat options
    reserved_seat: false,
    reserved_seat_category: '',
    reserved_seat_prefix: '',
    reserved_seat_section: false,
    seating_chart_id: null,
    // Membership fields
    set_member_number: false,
    prefix_text: '',
    suffix_text: '',
    last_member_number: '',
    // Sales windows toggle
    has_sales_windows: false,
    // Ticket-level tiers (for reserved seating without windows)
    tiers: [],
    // Sales windows
    windows: [],
    // Registration pages (only for type === 'registration')
    pages: [],
    pages_with_order: [],
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [toaster, setToaster] = useState({ isOpen: false });
  const [saving, setSaving] = useState(false);

  // Fetch waivers for pages
  const { data: waiversData } = useQuery(GET_WAIVERS);
  const waivers = waiversData?.getWaivers || [];

  // Fetch seating charts for reserved seating
  const { data: chartsData, loading: chartsLoading } =
    useQuery(GET_SEATING_CHARTS);
  const seatingChartOptions = (chartsData?.getSeatingCharts || []).map(
    (chart) => ({
      value: chart.id,
      label: chart.name,
    })
  );

  // Fetch Formstack forms for pages using the shared hook
  const {
    options: formstackOptions,
    loading: formstackLoading,
    handleSearch: handleFormstackSearch,
  } = useFormstackSearch();

  // Check if registration type is selected (needs step 4)
  const isRegistration = formData.type === 'registration';

  // Step configuration - conditionally include Pages step for registration
  const steps = isRegistration
    ? [
        { number: 1, label: 'Basic Info' },
        { number: 2, label: 'Pricing' },
        { number: 3, label: 'Options' },
        { number: 4, label: 'Pages' },
      ]
    : [
        { number: 1, label: 'Basic Info' },
        { number: 2, label: 'Pricing' },
        { number: 3, label: 'Options' },
      ];

  const totalSteps = steps.length;

  // Mutation hook
  const [addTicketTemplate] = useMutation(ADD_TICKET_TEMPLATE);

  // Determine route prefix for navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Apply default fees when price or type changes
  useEffect(() => {
    const numPrice = parseFloat(formData.price) || 0;
    if (numPrice < 1) return; // Only apply defaults for valid prices

    const { fee_dollar, fee_percent } = getDefaultTicketFees(
      formData.price,
      formData.type
    );
    const { pos_fee_dollar, pos_fee_percent } = getDefaultPosFees(
      formData.price
    );

    setFormData((prev) => ({
      ...prev,
      fee_dollar: fee_dollar !== undefined ? fee_dollar : prev.fee_dollar,
      fee_percent: fee_percent !== undefined ? fee_percent : prev.fee_percent,
      pos_fee_dollar:
        pos_fee_dollar !== undefined ? pos_fee_dollar : prev.pos_fee_dollar,
      pos_fee_percent:
        pos_fee_percent !== undefined ? pos_fee_percent : prev.pos_fee_percent,
    }));
  }, [formData.price, formData.type]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Handle attachment changes for description image
  const handleAttachmentsChange = (attachments) => {
    if (attachments.length > 0) {
      setFormData((prev) => ({
        ...prev,
        description_image_id: parseInt(attachments[0].id) || null,
        description_image_url: attachments[0].url,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        description_image_id: null,
        description_image_url: null,
      }));
    }
  };

  // Convert description_image to attachments array format for preview
  const getAttachmentsFromImage = () => {
    if (!formData.description_image_url) return [];
    return [
      {
        id: formData.description_image_id || 'description_image',
        name: 'Description Image',
        size: 0,
        sizeFormatted: '',
        url: formData.description_image_url,
        type: 'image/*',
        isImage: true,
      },
    ];
  };

  // Ticket-level tier handlers
  const addTier = () => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
    };
    setFormData((prev) => ({
      ...prev,
      tiers: [...prev.tiers, newTier],
    }));
  };

  const removeTier = (index) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  };

  const updateTier = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      ),
    }));
  };

  // Sales Windows handlers
  const addSalesWindow = () => {
    const newWindow = {
      id: `window_${Date.now()}`,
      name: '',
      ends: '',
      time: '23:59',
      price: '',
      unit: 'week', // Track unit for dynamic label
      tiers: [], // For reserved seat tiered pricing
    };
    setFormData((prev) => ({
      ...prev,
      windows: [...prev.windows, newWindow],
    }));
  };

  const removeSalesWindow = (index) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.filter((_, i) => i !== index),
    }));
  };

  const updateSalesWindow = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === index ? { ...window, [field]: value } : window
      ),
    }));
  };

  // Tier handlers for sales windows
  const addTierToWindow = (windowIndex) => {
    const newTier = {
      id: `tier_${Date.now()}`,
      name: '',
      price: '',
      track_price: '',
    };
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? { ...window, tiers: [...window.tiers, newTier] }
          : window
      ),
    }));
  };

  const removeTierFromWindow = (windowIndex, tierIndex) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? {
              ...window,
              tiers: window.tiers.filter((_, ti) => ti !== tierIndex),
            }
          : window
      ),
    }));
  };

  const updateTierInWindow = (windowIndex, tierIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      windows: prev.windows.map((window, i) =>
        i === windowIndex
          ? {
              ...window,
              tiers: window.tiers.map((tier, ti) =>
                ti === tierIndex ? { ...tier, [field]: value } : tier
              ),
            }
          : window
      ),
    }));
  };

  // Registration Pages handlers
  const addPage = () => {
    const newOrder =
      formData.pages.length > 0
        ? getHighestItemOrderNumber(formData.pages) + 1
        : 1;
    const newPage = {
      id: `page_${Date.now()}`,
      name: '',
      pdf_url: '',
      video_url: '',
      pdf_id: null,
      form_id: null,
      waivers: [],
      order: newOrder,
    };
    setFormData((prev) => ({
      ...prev,
      pages: [...prev.pages, newPage],
      pages_with_order: [...prev.pages_with_order, newPage],
    }));
  };

  const removePage = (index) => {
    const pageToRemove = formData.pages[index];
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.filter((_, i) => i !== index),
      pages_with_order: prev.pages_with_order.filter((p) =>
        pageToRemove.id
          ? p.id !== pageToRemove.id
          : p.name !== pageToRemove.name
      ),
    }));
  };

  const updatePage = (index, field, value) => {
    setFormData((prev) => {
      const updatedPages = prev.pages.map((page, i) =>
        i === index ? { ...page, [field]: value } : page
      );
      // Also update pages_with_order to keep them in sync
      const pageToUpdate = prev.pages[index];
      const updatedPagesWithOrder = prev.pages_with_order.map((p) =>
        (pageToUpdate.id && p.id === pageToUpdate.id) ||
        (!pageToUpdate.id && p.order === pageToUpdate.order)
          ? { ...p, [field]: value }
          : p
      );
      return {
        ...prev,
        pages: updatedPages,
        pages_with_order: updatedPagesWithOrder,
      };
    });
  };

  const addWaiverToPage = (pageIndex) => {
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex
          ? { ...page, waivers: [...(page.waivers || []), { waiver: '' }] }
          : page
      ),
    }));
  };

  const removeWaiverFromPage = (pageIndex, waiverIndex) => {
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.filter((_, wi) => wi !== waiverIndex),
            }
          : page
      ),
    }));
  };

  const updateWaiverInPage = (pageIndex, waiverIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.map((page, i) =>
        i === pageIndex
          ? {
              ...page,
              waivers: page.waivers.map((w, wi) =>
                wi === waiverIndex ? { waiver: value } : w
              ),
            }
          : page
      ),
    }));
  };

  const handlePagesSort = (sortedPages) => {
    // Update the order based on new positions
    const reorderedPages = sortedPages.map((page, index) => ({
      ...page,
      order: index + 1,
    }));
    setFormData((prev) => ({
      ...prev,
      pages_with_order: reorderedPages,
      // Also update the main pages array order
      pages: prev.pages.map((page) => {
        const sortedPage = reorderedPages.find((p) => p.id === page.id);
        return sortedPage ? { ...page, order: sortedPage.order } : page;
      }),
    }));
  };

  // Step navigation
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = 'Template name is required';
      }
    }

    if (step === 2) {
      if (!formData.price && formData.price !== 0) {
        newErrors.price = 'Price is required';
      } else if (
        isNaN(parseFloat(formData.price)) ||
        parseFloat(formData.price) < 0
      ) {
        newErrors.price = 'Price must be a valid positive number';
      }
    }

    if (step === 3) {
      if (!formData.category_id) {
        newErrors.category_id = 'Category is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const goToStep = (step) => {
    // Only allow going to completed steps or current step
    if (step < currentStep) {
      setCurrentStep(step);
    } else if (step === currentStep + 1 && validateStep(currentStep)) {
      setCurrentStep(step);
    }
  };

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    return 'pending';
  };

  const validateForm = () => {
    // Validate all steps
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData.price && formData.price !== 0) {
      newErrors.price = 'Price is required';
    } else if (
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) < 0
    ) {
      newErrors.price = 'Price must be a valid positive number';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    history.push(
      `${routePrefix}/accounts/editAccount/${accountId}?tab=tickets`
    );
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);

    // Process tiers - filter out empty ones and convert values
    const processedTiers = formData.tiers
      .filter((t) => t.name.trim())
      .map((t) => ({
        name: t.name.trim(),
        price: t.price ? parseFloat(t.price) : null,
        track_price: t.track_price ? parseFloat(t.track_price) : null,
      }));

    // Process windows - filter out empty ones and convert values
    const processedWindows = formData.windows
      .filter((w) => w.name.trim()) // Only include windows with a name
      .map((w) => ({
        name: w.name.trim(),
        ends: w.ends || '1 week before event',
        // Don't send time for hour-based windows (time is irrelevant for hours before gate time)
        time: w.unit === 'hour' ? null : w.time || '23:59',
        price: w.price ? parseFloat(w.price) : null,
        tiers: (w.tiers || [])
          .filter((t) => t.name.trim())
          .map((t) => ({
            name: t.name.trim(),
            price: t.price ? parseFloat(t.price) : null,
            track_price: t.track_price ? parseFloat(t.track_price) : null,
          })),
      }));

    // Process pages - only for registration type
    const processedPages = isRegistration
      ? formData.pages
          .filter((p) => p.name?.trim()) // Only include pages with a name
          .map((p) => ({
            name: p.name.trim(),
            pdf_url: p.pdf_url?.trim() || null,
            video_url: p.video_url?.trim() || null,
            pdf_id: p.pdf_id ? parseInt(p.pdf_id) : null,
            form_id: p.form_id ? p.form_id.toString() : null,
            waivers: (p.waivers || [])
              .filter((w) => w.waiver)
              .map((w) => ({ waiver_id: parseInt(w.waiver) })),
            order: p.order || 1,
          }))
      : null;

    const input = {
      account_id: parseInt(accountId),
      name: formData.name.trim(),
      type: formData.type,
      price: parseFloat(formData.price) || 0,
      track_price: formData.track_price
        ? parseFloat(formData.track_price)
        : null,
      description: formData.description.trim() || null,
      description_image_id: formData.description_image_id || null,
      description_video: formData.description_video.trim() || null,
      fee_dollar: formData.fee_dollar ? parseFloat(formData.fee_dollar) : null,
      fee_percent: formData.fee_percent
        ? parseFloat(formData.fee_percent)
        : null,
      pos_fee_dollar: formData.pos_fee_dollar
        ? parseFloat(formData.pos_fee_dollar)
        : null,
      pos_fee_percent: formData.pos_fee_percent
        ? parseFloat(formData.pos_fee_percent)
        : null,
      visibility: formData.visibility,
      limit: formData.limit ? parseInt(formData.limit) : null,
      reserved_seat: formData.reserved_seat,
      reserved_seat_category: formData.reserved_seat_category.trim() || null,
      reserved_seat_prefix: formData.reserved_seat_prefix.trim() || null,
      reserved_seat_section: formData.reserved_seat_section,
      chart_id: formData.seating_chart_id || null,
      category_id: formData.category_id ? parseInt(formData.category_id) : null,
      set_member_number: formData.set_member_number || false,
      prefix_text: formData.prefix_text?.trim() || null,
      suffix_text: formData.suffix_text?.trim() || null,
      last_member_number: formData.last_member_number
        ? parseInt(formData.last_member_number)
        : null,
      tiers: processedTiers.length > 0 ? processedTiers : null,
      windows: processedWindows.length > 0 ? processedWindows : null,
      pages:
        processedPages && processedPages.length > 0 ? processedPages : null,
    };

    try {
      const { data } = await addTicketTemplate({ variables: { input } });

      if (data?.addTicketTemplate) {
        // Navigate back with success message
        history.push({
          pathname: `${routePrefix}/accounts/editAccount/${accountId}`,
          search: '?tab=tickets',
          state: {
            toaster: {
              type: 'success',
              title: 'Template Created',
              message: `"${formData.name.trim()}" has been created successfully.`,
            },
          },
        });
      }
    } catch (error) {
      setSaving(false);
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error Creating Template',
        message:
          error.message ||
          'Failed to create ticket template. Please try again.',
      });
    }
  };

  return (
    <PageWrapper>
      <Container>
        <BackButtonWrapper>
          <ButtonV2 type="button" onClick={handleBack} icon={<ArrowLeftIcon />}>
            Back to Account
          </ButtonV2>
        </BackButtonWrapper>

        <PageHeader>
          <HeaderLeft>
            <Title>Add Ticket Template</Title>
            <Subtitle>
              {currentStep === 1 &&
                'Set up the basic details for your ticket template'}
              {currentStep === 2 && 'Configure pricing and sales options'}
              {currentStep === 3 && 'Set additional options and settings'}
              {currentStep === 4 && 'Configure registration pages for checkout'}
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButtonWrapper>
              <ButtonV2 type="button" onClick={handleBack}>
                Cancel
              </ButtonV2>
            </HeaderButtonWrapper>
          </HeaderRight>
        </PageHeader>

        {/* Step Indicator */}
        <StepperContainer>
          <StepperTrack>
            {steps.map((step, index) => (
              <StepItem key={step.number}>
                <StepCircle
                  type="button"
                  status={getStepStatus(step.number)}
                  isClickable={step.number < currentStep}
                  onClick={() => goToStep(step.number)}
                >
                  {getStepStatus(step.number) === 'completed' ? (
                    <CheckIcon />
                  ) : (
                    step.number
                  )}
                </StepCircle>
                <StepLabel status={getStepStatus(step.number)}>
                  {step.label}
                </StepLabel>
                {index < steps.length - 1 && (
                  <StepConnector completed={step.number < currentStep} />
                )}
              </StepItem>
            ))}
          </StepperTrack>
        </StepperContainer>

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>

              <FormRow columns="2fr 1fr">
                <FormGroup>
                  <Label>
                    Template Name
                    <RequiredBadge>*Required</RequiredBadge>
                  </Label>
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="e.g., General Admission"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={!!errors.name}
                  />
                  {errors.name && <ErrorText>{errors.name}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label>Type</Label>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                  >
                    {TICKET_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Description</Label>
                <Textarea
                  placeholder="Enter ticket description..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
              </FormGroup>

              <FormRow columns="1fr 1fr" style={{ marginTop: '16px' }}>
                <FormGroup>
                  <Label>Description Image</Label>
                  <AttachmentUpload
                    attachments={getAttachmentsFromImage()}
                    onAttachmentsChange={handleAttachmentsChange}
                    maxFiles={1}
                    maxFileSize={5242880}
                    channel="SMS"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Description URL (Video)</Label>
                  <Input
                    type="url"
                    placeholder="e.g., https://vimeo.com/... or https://youtube.com/..."
                    value={formData.description_video}
                    onChange={(e) =>
                      handleChange('description_video', e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              {/* Ticket Type Toggles - Two Column Layout */}
              <TwoColumnGrid style={{ marginTop: '24px' }}>
                <ToggleCard>
                  <ToggleInfo>
                    <ToggleTitle>Reserved Seating</ToggleTitle>
                    <ToggleDescription>
                      Enable Seats.io integration for seat selection
                    </ToggleDescription>
                  </ToggleInfo>
                  <Toggle
                    checked={formData.reserved_seat}
                    onChange={(e) =>
                      handleChange('reserved_seat', e.target.checked)
                    }
                  />
                </ToggleCard>

                <ToggleCard>
                  <ToggleInfo>
                    <ToggleTitle>Sales Windows</ToggleTitle>
                    <ToggleDescription>
                      Create time-based pricing periods (e.g., Early Bird)
                    </ToggleDescription>
                  </ToggleInfo>
                  <Toggle
                    checked={formData.has_sales_windows}
                    onChange={(e) =>
                      handleChange('has_sales_windows', e.target.checked)
                    }
                  />
                </ToggleCard>
              </TwoColumnGrid>

              {/* Step Navigation */}
              <StepNavigation>
                <div />
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    Continue to Pricing
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 2: Pricing */}
        {currentStep === 2 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Pricing</SectionTitle>

              <FormRow columns="1fr 1fr">
                <FormGroup>
                  <Label>
                    Price
                    <RequiredBadge>*Required</RequiredBadge>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    error={!!errors.price}
                  />
                  {errors.price && <ErrorText>{errors.price}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label>At Track Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.track_price}
                    onChange={(e) =>
                      handleChange('track_price', e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              {/* Fees */}
              <SectionTitle style={{ marginTop: '24px' }}>Fees</SectionTitle>
              <FormRow columns="1fr 1fr 1fr 1fr">
                <FormGroup>
                  <Label>Fee Dollar Amount (App)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.fee_dollar}
                    onChange={(e) => handleChange('fee_dollar', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <LabelWithTooltip>
                    Fee Percent
                    <Tooltip
                      info
                      content="This percentage needs to include the 3.25% base processing fee."
                      iconColor={infoIconColor}
                      iconSize="16px"
                    />
                  </LabelWithTooltip>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.fee_percent}
                    onChange={(e) =>
                      handleChange('fee_percent', e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Fee Dollar Amount (POS)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.pos_fee_dollar}
                    onChange={(e) =>
                      handleChange('pos_fee_dollar', e.target.value)
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <LabelWithTooltip>
                    Fee Percent (POS)
                    <Tooltip
                      info
                      content="This percentage needs to include the 3.25% base processing fee."
                      iconColor={infoIconColor}
                      iconSize="16px"
                    />
                  </LabelWithTooltip>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.pos_fee_percent}
                    onChange={(e) =>
                      handleChange('pos_fee_percent', e.target.value)
                    }
                  />
                </FormGroup>
              </FormRow>

              {/* Tiered Pricing - For reserved seats (coexists with sales window tiers) */}
              {formData.reserved_seat && (
                <TierSection>
                  <TierHeader>
                    <TierTitle>Tiered Pricing</TierTitle>
                    <AddTierButtonWrapper>
                      <ButtonV2
                        elevated
                        type="button"
                        onClick={addTier}
                        icon={<PlusIcon />}
                      >
                        Add
                      </ButtonV2>
                    </AddTierButtonWrapper>
                  </TierHeader>

                  {formData.tiers.length === 0 ? (
                    <EmptyTiersMessage>
                      No pricing tiers configured. Add tiers for different seat
                      categories.
                    </EmptyTiersMessage>
                  ) : (
                    <>
                      <TierRow>
                        <Label>Label (Name)</Label>
                        <Label>App Price</Label>
                        <Label>Track Price</Label>
                        <div />
                      </TierRow>
                      {formData.tiers.map((tier, index) => (
                        <TierRow key={tier.id}>
                          <Input
                            type="text"
                            placeholder="e.g., Front Row"
                            value={tier.name}
                            onChange={(e) =>
                              updateTier(index, 'name', e.target.value)
                            }
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={tier.price}
                            onChange={(e) =>
                              updateTier(index, 'price', e.target.value)
                            }
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={tier.track_price}
                            onChange={(e) =>
                              updateTier(index, 'track_price', e.target.value)
                            }
                          />
                          <RemoveTierButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removeTier(index)}
                              title="Remove tier"
                              icon={<XIcon />}
                            />
                          </RemoveTierButtonWrapper>
                        </TierRow>
                      ))}
                    </>
                  )}
                </TierSection>
              )}

              {/* Sales Windows - Show when has_sales_windows is enabled */}
              {formData.has_sales_windows && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Sales Windows
                  </SectionTitle>
                  <SectionDescription>
                    Create promotional pricing periods. Each window ends at the
                    specified time relative to the event date.
                  </SectionDescription>

                  <SalesWindowsContainer>
                    {formData.windows.length === 0 ? (
                      <EmptyWindowsMessage>
                        No sales windows configured. Add one to enable
                        time-based pricing.
                      </EmptyWindowsMessage>
                    ) : (
                      formData.windows.map((window, windowIndex) => (
                        <SalesWindowCard key={window.id}>
                          <SalesWindowHeader>
                            <SalesWindowTitle>
                              {window.name || `Window ${windowIndex + 1}`}
                            </SalesWindowTitle>
                            <SmallIconButtonWrapper>
                              <ButtonV2
                                danger
                                variant="icon"
                                type="button"
                                onClick={() => removeSalesWindow(windowIndex)}
                                title="Remove window"
                                icon={<XIcon />}
                              />
                            </SmallIconButtonWrapper>
                          </SalesWindowHeader>

                          <FormRow
                            columns={
                              formData.reserved_seat
                                ? '1fr auto'
                                : '1fr auto 100px'
                            }
                          >
                            <FormGroup>
                              <Label>Window Name</Label>
                              <Input
                                type="text"
                                placeholder="e.g., Early Bird"
                                value={window.name}
                                onChange={(e) =>
                                  updateSalesWindow(
                                    windowIndex,
                                    'name',
                                    e.target.value
                                  )
                                }
                              />
                            </FormGroup>

                            <FormGroup>
                              <Label>
                                Ends (before{' '}
                                {window.unit === 'hour' ? 'gate time' : 'event'}
                                )
                              </Label>
                              <RelativeDatePicker
                                value={window.ends || '1 week before event'}
                                onChange={(value) =>
                                  updateSalesWindow(windowIndex, 'ends', value)
                                }
                                time={window.time || '23:59'}
                                onTimeChange={(time) =>
                                  updateSalesWindow(windowIndex, 'time', time)
                                }
                                onUnitChange={(unit) =>
                                  updateSalesWindow(windowIndex, 'unit', unit)
                                }
                              />
                            </FormGroup>

                            {/* Show price field if not reserved OR if reserved but no tiers yet */}
                            {(!formData.reserved_seat ||
                              window.tiers.length === 0) && (
                              <FormGroup>
                                <Label>Price</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={window.price}
                                  onChange={(e) =>
                                    updateSalesWindow(
                                      windowIndex,
                                      'price',
                                      e.target.value
                                    )
                                  }
                                />
                              </FormGroup>
                            )}
                          </FormRow>

                          {/* Tiers inside window - only for reserved seats */}
                          {formData.reserved_seat && (
                            <TierSection style={{ marginTop: '16px' }}>
                              <TierHeader>
                                <TierTitle style={{ fontSize: '13px' }}>
                                  Pricing Tiers
                                </TierTitle>
                                <AddTierButtonWrapper>
                                  <ButtonV2
                                    elevated
                                    type="button"
                                    onClick={() => addTierToWindow(windowIndex)}
                                    icon={<PlusIcon />}
                                  >
                                    Add
                                  </ButtonV2>
                                </AddTierButtonWrapper>
                              </TierHeader>

                              {window.tiers.length === 0 ? (
                                <EmptyTiersMessage>
                                  No tiers. Add tiers for seat-based pricing.
                                </EmptyTiersMessage>
                              ) : (
                                <>
                                  <TierRow>
                                    <Label>Label</Label>
                                    <Label>App Price</Label>
                                    <Label>Track Price</Label>
                                    <div />
                                  </TierRow>
                                  {window.tiers.map((tier, tierIndex) => (
                                    <TierRow key={tier.id}>
                                      <Input
                                        type="text"
                                        placeholder="e.g., Front Row"
                                        value={tier.name}
                                        onChange={(e) =>
                                          updateTierInWindow(
                                            windowIndex,
                                            tierIndex,
                                            'name',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={tier.price}
                                        onChange={(e) =>
                                          updateTierInWindow(
                                            windowIndex,
                                            tierIndex,
                                            'price',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={tier.track_price}
                                        onChange={(e) =>
                                          updateTierInWindow(
                                            windowIndex,
                                            tierIndex,
                                            'track_price',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <RemoveTierButtonWrapper>
                                        <ButtonV2
                                          danger
                                          variant="icon"
                                          type="button"
                                          onClick={() =>
                                            removeTierFromWindow(
                                              windowIndex,
                                              tierIndex
                                            )
                                          }
                                          title="Remove tier"
                                          icon={<XIcon />}
                                        />
                                      </RemoveTierButtonWrapper>
                                    </TierRow>
                                  ))}
                                </>
                              )}
                            </TierSection>
                          )}
                        </SalesWindowCard>
                      ))
                    )}

                    <AddWindowButtonWrapper>
                      <ButtonV2
                        type="button"
                        onClick={addSalesWindow}
                        icon={<PlusIcon />}
                      >
                        Add Sales Window
                      </ButtonV2>
                    </AddWindowButtonWrapper>
                  </SalesWindowsContainer>
                </>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={handlePrevStep}
                    icon={<ArrowLeftIcon />}
                  >
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2 elevated type="button" onClick={handleNextStep}>
                    Continue to Options
                    <ArrowRightIcon />
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 3: Additional Options */}
        {currentStep === 3 && (
          <FormCard>
            <FormSection>
              <SectionTitle>Additional Options</SectionTitle>

              <FormRow columns="1fr 1fr 1fr">
                <FormGroup>
                  <Label>Visibility</Label>
                  <Select
                    value={formData.visibility}
                    onChange={(e) => handleChange('visibility', e.target.value)}
                  >
                    {VISIBILITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <CategorySelect
                    value={formData.category_id}
                    onChange={(option) =>
                      handleChange('category_id', option?.value || '')
                    }
                    error={!!errors.category_id}
                    onSuccess={(name) =>
                      setToaster({
                        isOpen: true,
                        type: 'success',
                        title: 'Category Created',
                        message: `"${name}" has been added.`,
                      })
                    }
                    onError={() =>
                      setToaster({
                        isOpen: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to create category. Please try again.',
                      })
                    }
                  />
                  {errors.category_id && (
                    <ErrorText>{errors.category_id}</ErrorText>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Ticket Limit</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="No limit"
                    value={formData.limit}
                    onChange={(e) => handleChange('limit', e.target.value)}
                  />
                </FormGroup>
              </FormRow>

              {/* Reserved Seating Options - Only shown when reserved_seat is enabled */}
              {formData.reserved_seat && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Reserved Seating Settings
                  </SectionTitle>

                  <FormRow columns="1fr 1fr">
                    <FormGroup>
                      <Label>Seats.io Category</Label>
                      <Input
                        type="text"
                        placeholder="Enter category"
                        value={formData.reserved_seat_category}
                        onChange={(e) =>
                          handleChange('reserved_seat_category', e.target.value)
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Ticket Prefix</Label>
                      <Input
                        type="text"
                        placeholder="Enter prefix"
                        value={formData.reserved_seat_prefix}
                        onChange={(e) =>
                          handleChange('reserved_seat_prefix', e.target.value)
                        }
                      />
                    </FormGroup>
                  </FormRow>

                  <TwoColumnGrid style={{ marginTop: '16px', marginBottom: 0 }}>
                    <FormGroup>
                      <Label>Seating Chart</Label>
                      <SearchableSelect
                        value={formData.seating_chart_id}
                        onChange={(option) =>
                          handleChange(
                            'seating_chart_id',
                            option?.value || null
                          )
                        }
                        options={seatingChartOptions}
                        loading={chartsLoading}
                        placeholder="Select a seating chart..."
                        isClearable
                      />
                    </FormGroup>

                    <ToggleRow>
                      <ToggleInfo>
                        <ToggleTitle>Show as Section/Row/Seat</ToggleTitle>
                        <ToggleDescription>
                          Display seat information in section, row, seat format
                        </ToggleDescription>
                      </ToggleInfo>
                      <Toggle
                        checked={formData.reserved_seat_section}
                        onChange={(e) =>
                          handleChange(
                            'reserved_seat_section',
                            e.target.checked
                          )
                        }
                      />
                    </ToggleRow>
                  </TwoColumnGrid>
                </>
              )}

              {/* Member Numbers - Only for registration/membership types */}
              {isRegistration && (
                <>
                  <SectionTitle style={{ marginTop: '24px' }}>
                    Member Numbers
                  </SectionTitle>
                  <SectionDescription>
                    Configure automatic member number assignment for purchasers
                  </SectionDescription>

                  <ToggleRow>
                    <ToggleInfo>
                      <ToggleTitle>Set Member Numbers</ToggleTitle>
                      <ToggleDescription>
                        Automatically assign member numbers to purchasers
                      </ToggleDescription>
                    </ToggleInfo>
                    <Toggle
                      checked={formData.set_member_number}
                      onChange={(e) =>
                        handleChange('set_member_number', e.target.checked)
                      }
                    />
                  </ToggleRow>

                  {formData.set_member_number && (
                    <>
                      <FormRow columns="1fr 1fr" style={{ marginTop: '16px' }}>
                        <FormGroup>
                          <LabelWithTooltip>
                            Prefix Text
                            <Tooltip
                              info
                              content="Text that appears before the member number. For example, entering '2026' would produce member numbers like '2026-1'."
                              iconColor={infoIconColor}
                              iconSize="16px"
                            />
                          </LabelWithTooltip>
                          <Input
                            type="text"
                            value={formData.prefix_text}
                            onChange={(e) =>
                              handleChange('prefix_text', e.target.value)
                            }
                            placeholder="e.g., 2026"
                          />
                        </FormGroup>

                        <FormGroup>
                          <LabelWithTooltip>
                            Suffix Text
                            <Tooltip
                              info
                              content="Text that appears after the member number. For example, entering 'D' would produce member numbers like '1-D'."
                              iconColor={infoIconColor}
                              iconSize="16px"
                            />
                          </LabelWithTooltip>
                          <Input
                            type="text"
                            value={formData.suffix_text}
                            onChange={(e) =>
                              handleChange('suffix_text', e.target.value)
                            }
                            placeholder="e.g., D"
                          />
                        </FormGroup>
                      </FormRow>

                      <FormRow>
                        <FormGroup>
                          <LabelWithTooltip>
                            Starting Member Number
                            <Tooltip
                              info
                              content="The next member number to be assigned. Numbers will increment from this value."
                              iconColor={infoIconColor}
                              iconSize="16px"
                            />
                          </LabelWithTooltip>
                          <Input
                            type="number"
                            value={formData.last_member_number}
                            onChange={(e) =>
                              handleChange('last_member_number', e.target.value)
                            }
                            placeholder="1"
                            min="1"
                          />
                        </FormGroup>
                      </FormRow>

                      <SectionDescription style={{ marginTop: '16px' }}>
                        <strong>Preview:</strong>{' '}
                        {[
                          formData.prefix_text,
                          formData.last_member_number || '1',
                          formData.suffix_text,
                        ]
                          .filter(Boolean)
                          .join('-')}
                      </SectionDescription>
                    </>
                  )}
                </>
              )}

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={handlePrevStep}
                    icon={<ArrowLeftIcon />}
                  >
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                {isRegistration ? (
                  <NavButtonWrapper>
                    <ButtonV2 elevated type="button" onClick={handleNextStep}>
                      Continue to Pages
                      <ArrowRightIcon />
                    </ButtonV2>
                  </NavButtonWrapper>
                ) : (
                  <NavButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Creating...' : 'Create Template'}
                    </ButtonV2>
                  </NavButtonWrapper>
                )}
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}

        {/* Step 4: Registration Pages - Only shown for registration type */}
        {currentStep === 4 && isRegistration && (
          <FormCard>
            <FormSection>
              <SectionTitle>Registration Pages</SectionTitle>
              <SectionDescription>
                Add pages that will be displayed during checkout. Pages can
                include PDFs, videos, forms, and waivers.
              </SectionDescription>

              <PagesContainer>
                <PagesListSection>
                  {formData.pages.length === 0 ? (
                    <EmptyPagesMessage>
                      No pages configured yet. Add a page to get started.
                    </EmptyPagesMessage>
                  ) : (
                    formData.pages.map((page, pageIndex) => (
                      <PageCard key={page.id}>
                        <PageCardHeader>
                          <PageCardTitle>
                            {page.name || `Page ${pageIndex + 1}`}
                          </PageCardTitle>
                          <DeletePageButtonWrapper>
                            <ButtonV2
                              danger
                              variant="icon"
                              type="button"
                              onClick={() => removePage(pageIndex)}
                              title="Remove page"
                              icon={<TrashIcon />}
                            />
                          </DeletePageButtonWrapper>
                        </PageCardHeader>

                        <FormRow columns="1fr">
                          <FormGroup>
                            <Label>Page Name</Label>
                            <Input
                              type="text"
                              placeholder="Enter page name"
                              value={page.name || ''}
                              onChange={(e) =>
                                updatePage(pageIndex, 'name', e.target.value)
                              }
                            />
                          </FormGroup>
                        </FormRow>

                        <FormGroup style={{ marginBottom: '16px' }}>
                          <Label>PDF Upload</Label>
                          <AttachmentUpload
                            attachments={
                              page.pdf_id &&
                              (page.pdf_url || page.pdf_upload_url)
                                ? [
                                    {
                                      id: page.pdf_id,
                                      name: 'Uploaded PDF',
                                      size: 0,
                                      sizeFormatted: '',
                                      url: page.pdf_url || page.pdf_upload_url,
                                      type: 'application/pdf',
                                      isPDF: true,
                                    },
                                  ]
                                : []
                            }
                            onAttachmentsChange={(attachments) => {
                              if (attachments.length > 0) {
                                updatePage(
                                  pageIndex,
                                  'pdf_id',
                                  attachments[0].id
                                );
                                updatePage(
                                  pageIndex,
                                  'pdf_url',
                                  attachments[0].url
                                );
                              } else {
                                updatePage(pageIndex, 'pdf_id', null);
                                updatePage(pageIndex, 'pdf_url', null);
                              }
                            }}
                            maxFiles={1}
                            maxFileSize={10485760}
                            channel="SMS"
                          />
                        </FormGroup>

                        <FormRow columns="1fr 1fr">
                          <FormGroup>
                            <Label>Video URL</Label>
                            <Input
                              type="url"
                              placeholder="https://..."
                              value={page.video_url || ''}
                              onChange={(e) =>
                                updatePage(
                                  pageIndex,
                                  'video_url',
                                  e.target.value
                                )
                              }
                            />
                          </FormGroup>

                          <FormGroup>
                            <Label>Form ID</Label>
                            <SearchableSelect
                              value={
                                page.form_id ? parseInt(page.form_id) : null
                              }
                              onChange={(option) => {
                                updatePage(
                                  pageIndex,
                                  'form_id',
                                  option?.value?.toString() || null
                                );
                              }}
                              options={formstackOptions}
                              loading={formstackLoading}
                              onSearch={handleFormstackSearch}
                              minSearchLength={4}
                              placeholder="Type to search forms..."
                              noResultsText="No forms found"
                            />
                          </FormGroup>
                        </FormRow>

                        <FormGroup style={{ marginTop: '16px' }}>
                          <Label>Waivers</Label>
                          <WaiversContainer>
                            {(page.waivers || []).length === 0 ? (
                              <EmptyText>No waivers added</EmptyText>
                            ) : (
                              page.waivers.map((waiver, waiverIndex) => (
                                <WaiverRow key={waiverIndex}>
                                  <Select
                                    value={waiver.waiver || ''}
                                    onChange={(e) =>
                                      updateWaiverInPage(
                                        pageIndex,
                                        waiverIndex,
                                        e.target.value
                                      )
                                    }
                                    style={{ flex: 1 }}
                                  >
                                    <option value="">Select a waiver...</option>
                                    {waivers.map((w) => (
                                      <option key={w.id} value={w.id}>
                                        {w.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <RemoveWaiverButtonWrapper>
                                    <ButtonV2
                                      danger
                                      variant="icon"
                                      type="button"
                                      onClick={() =>
                                        removeWaiverFromPage(
                                          pageIndex,
                                          waiverIndex
                                        )
                                      }
                                      title="Remove waiver"
                                      icon={<MinusIcon />}
                                    />
                                  </RemoveWaiverButtonWrapper>
                                </WaiverRow>
                              ))
                            )}
                            <AddWaiverButtonWrapper>
                              <ButtonV2
                                elevated
                                type="button"
                                onClick={() => addWaiverToPage(pageIndex)}
                                icon={<PlusIcon />}
                              >
                                Add another waiver
                              </ButtonV2>
                            </AddWaiverButtonWrapper>
                          </WaiversContainer>
                        </FormGroup>
                      </PageCard>
                    ))
                  )}

                  <AddPageButtonWrapper>
                    <ButtonV2
                      elevated
                      type="button"
                      onClick={addPage}
                      icon={<PlusIcon />}
                    >
                      {formData.pages.length > 0
                        ? 'ADD ANOTHER PAGE'
                        : 'ADD PAGE'}
                    </ButtonV2>
                  </AddPageButtonWrapper>
                </PagesListSection>

                {formData.pages_with_order.length > 0 && (
                  <PagesOrderSection>
                    <OrderSectionTitle>Pages Order In App</OrderSectionTitle>
                    <ReactSortable
                      list={getOrderedItems(formData.pages_with_order)}
                      setList={handlePagesSort}
                    >
                      {getOrderedItems(formData.pages_with_order).map(
                        (page) => (
                          <DraggablePageItem key={page.id || page.order}>
                            <DragHandle>
                              <DragHandleIcon />
                            </DragHandle>
                            <PageOrderName>
                              {page.name || 'Unnamed Page'}
                            </PageOrderName>
                          </DraggablePageItem>
                        )
                      )}
                    </ReactSortable>
                  </PagesOrderSection>
                )}
              </PagesContainer>

              {/* Step Navigation */}
              <StepNavigation>
                <NavButtonWrapper>
                  <ButtonV2
                    type="button"
                    onClick={handlePrevStep}
                    icon={<ArrowLeftIcon />}
                  >
                    Back
                  </ButtonV2>
                </NavButtonWrapper>
                <NavButtonWrapper>
                  <ButtonV2
                    elevated
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Creating...' : 'Create Template'}
                  </ButtonV2>
                </NavButtonWrapper>
              </StepNavigation>
            </FormSection>
          </FormCard>
        )}
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const AddTicketTemplate = () => {
  return (
    <EnhancedThemeProvider>
      <AddTicketTemplateContent />
    </EnhancedThemeProvider>
  );
};

export default AddTicketTemplate;
